import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, AlertTriangle } from "lucide-react";
import { getVacantPeriods } from "../../api/timetable/timetableApi";
import { getVacantPeriodsPortal } from "../../api/timetable/timetablePortalApi";
import { Badge } from "../ui";
import WidgetCard from "./WidgetCard";
import { useToast } from "../../context/ToastContext";

function getInitial(name) {
  if (!name || typeof name !== "string") return "?";
  return name.trim().charAt(0).toUpperCase();
}

export default function TodayClassStatusWidget({ limit = 3, isPortal = false }) {
  const navigate = useNavigate();

  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await getVacantPeriodsPortal() : await getVacantPeriods();
      const list = Array.isArray(res) ? res : res.data || res.vacant || [];
      setPeriods(list.slice(0, limit));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load timetable status");
    } finally {
      setLoading(false);
    }
  }, [limit, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <WidgetCard
      title="Today's Class Status"
      icon={CalendarClock}
      loading={loading}
      empty={!loading && periods.length === 0}
      emptyText="No vacant periods right now — all classes covered."
      onViewAll={() => navigate("/timetable")}
    >
      

      <div className="space-y-2">
        {periods.map((p, idx) => {
          const teacherName = p.staffId?.name || "";
          return (
            <div
              key={p._id || idx}
              className="flex items-center gap-3 rounded-xl border border-rose-200/80 bg-gradient-to-r from-rose-50/40 via-white to-rose-50/20 px-3.5 py-2.5 transition-all hover:shadow-xs"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-xs font-bold text-rose-800 ring-1 ring-rose-500/20">
                {getInitial(teacherName)}
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-slate-900 dark:text-neutral-100">
                  {p.classId?.className} - {p.sectionId?.sectionName}
                  {p.subjectId?.name ? <span className="text-indigo-600 font-semibold">{` · ${p.subjectId.name}`}</span> : ""}
                </p>
                <p className="truncate text-[11px] font-medium text-slate-500 dark:text-neutral-400">
                  <span className="font-bold text-slate-700 dark:text-neutral-200">P{p.periodNumber}</span> · {p.startTime}–{p.endTime}
                  {teacherName ? ` · ${teacherName}` : ""}
                </p>
              </div>

              <Badge
                variant="danger"
                className="shrink-0 gap-1 text-[10px] px-2 py-0.5 font-bold bg-rose-100 text-rose-800 border-rose-200 uppercase tracking-wider"
              >
                <AlertTriangle className="h-3 w-3 text-rose-600" />
                Vacant
              </Badge>
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
}